// cookie-manager.ts
import axios from 'axios';
import store from '@/store';
import {bindConsent, getPixelIdForPath} from '@/helpers/meta/metaTrack';
import {MetaEventParamsMap, MetaTrackEvents} from '@/helpers/meta/metaEvents';
import {
    bindDestinationsConsent,
    GA4_COOKIE_NAMES,
    GA4_COOKIE_PREFIXES,
    ga4Track,
    TIKTOK_COOKIE_NAMES,
    TIKTOK_COOKIE_PREFIXES,
    tiktokIdentify,
    tiktokTrack,
    TikTokIdentifyPii,
} from '@/helpers/tracking/destinations';
import {getTrackingPath} from '@/helpers/tracking/getTrackingPath';
import {
    buildSettings,
    ConsentCategory,
    ConsentChoices,
    COOKIE_SETTINGS_KEY,
    CookieSettings,
    parseStoredSettings,
    removeCookies,
    removeSignupSessionData,
    setConsentState,
} from '@/helpers/tracking/consent';

type ConsentListener = (choices: ConsentChoices) => void;

/** Von Meta Pixel gesetzte First-Party-Cookies */
const META_COOKIE_NAMES = ['_fbp', '_fbc'];

/* -------------------- Tracker (CAPI proxy) Typen -------------------- */
type TrackerUserData = {
    fbp?: string;
    fbc?: string;
    // IP & UA setzt/hasht der Server aus Request-Headern
    external_id?: string;              // optional (falls vorhanden)
};
type TrackerEvent = {
    event_name: string;
    event_time: number;                // UNIX seconds
    event_id: string;                  // vom Client generiert
    action_source: 'website';
    pixel_id: string;
    event_source_url?: string;
    user_data: TrackerUserData;
    custom_data: Record<string, unknown>;
};

function onSignupPath(path: string): boolean {
    return path === '/anmelden' || path.startsWith('/anmelden/');
}

function onRecruitingPath(path: string): boolean {
    return path === '/bewerben' || path.startsWith('/bewerben/');
}

export default class CookieManager {
    /** Gespeicherte Entscheidung (zum Vorbelegen des Banners) */
    private _cookies: CookieSettings = buildSettings({statistics: false, marketing: false}, null, 0);
    /** Wirksame Einwilligung (steuert sämtliches Tracking) */
    private _consent: ConsentChoices = {statistics: false, marketing: false};
    private _listeners: ConsentListener[] = [];
    /** Signup-Session-IDs/Zähler ohne Einwilligung nur im Speicher halten (kein Schreiben ins Endgerät) */
    private _memorySids: Record<string, string> = {};
    /** Liegt eine aktuelle Entscheidung vor (sonst Banner)? */
    private _decided = false;

    constructor() {
        let needsPrompt = true;
        try {
            const parsed = parseStoredSettings(localStorage.getItem(COOKIE_SETTINGS_KEY));
            this._cookies = parsed.settings;
            this._consent = parsed.effective;
            needsPrompt = parsed.needsPrompt;
            this._decided = !parsed.needsPrompt;
        } catch {
            // localStorage nicht verfügbar → keine Einwilligung, Banner zeigen
        }
        setConsentState(this._consent);
        if (needsPrompt) setTimeout(() => this.showCookieDialog(), 500);

        // Meta-Pixel Consent Binding (Kategorie Marketing)
        bindConsent(
            () => this._consent.marketing,
            (cb) => this.onConsentChange((c) => cb(c.marketing)),
        );

        // GA4 (Statistik) & TikTok (Marketing) Consent Binding (nur auf /bewerben*, siehe destinations.ts)
        bindDestinationsConsent(
            () => ({...this._consent}),
            (cb) => this.onConsentChange(cb),
        );

        // PageView nachfeuern, falls er VOR der Einwilligung gefeuert und
        // deshalb verworfen wurde (Banner-Accept kommt nach dem View-Init).
        // Das Sent-Set von fbqTrackOnce verhindert eine Doppelzählung.
        this.onConsentChange((c) => {
            if (!c.marketing && !c.statistics) return;
            this.fbqTrackOnce('Page View - Signup', MetaTrackEvents.PAGE_VIEW);
        });
    }

    /* -------------------- Consent UI -------------------- */

    public onConsentChange(fn: ConsentListener): void {
        this._listeners.push(fn);
    }

    private _emit(): void {
        const choices = {...this._consent};
        for (const fn of this._listeners) {
            try {
                fn(choices);
            } catch { /* noop */
            }
        }
    }

    public showCookieDialog(): void {
        store.commit('showCookieDialog', true);
    }

    public hideCookieDialog(): void {
        store.commit('showCookieDialog', false);
    }

    /** Gespeicherte Auswahl inkl. Zeitstempel/Revision (Kopie) */
    public getCookieSettings(): CookieSettings {
        return {...this._cookies};
    }

    /** Wirksame Einwilligung für eine Kategorie */
    public hasConsent(category: ConsentCategory): boolean {
        return !!this._consent[category];
    }

    /** Hat die Person bereits eine (aktuelle) Entscheidung getroffen? */
    public hasDecided(): boolean {
        return this._decided;
    }

    /**
     * Auswahl speichern und sofort anwenden. Widerrufene Kategorien werden
     * gestoppt und ihre Cookies – soweit technisch möglich – gelöscht.
     */
    public async saveConsent(choices: ConsentChoices): Promise<void> {
        const previous = {...this._consent};
        const next: ConsentChoices = {statistics: !!choices.statistics, marketing: !!choices.marketing};

        this._cookies = buildSettings(next, new Date().toISOString());
        this._consent = next;
        this._decided = true;
        setConsentState(next);
        this._save();

        try {
            this._applyRevocations(previous, next);
            this._applyMetaConsent(next);
            this._emit();
        } finally {
            this.hideCookieDialog();
        }

        await this._syncServerCookie(next.statistics);
    }

    public acceptAll(): Promise<void> {
        return this.saveConsent({statistics: true, marketing: true});
    }

    public rejectAll(): Promise<void> {
        return this.saveConsent({statistics: false, marketing: false});
    }

    /** @deprecated Alias für acceptAll() (Abwärtskompatibilität) */
    public setTrackingCookie(): Promise<void> {
        return this.acceptAll();
    }

    /** @deprecated Alias für rejectAll() (Abwärtskompatibilität) */
    public resetTrackingCookie(): Promise<void> {
        return this.rejectAll();
    }

    /** Eigenes Formular-Statistik-Cookie ("analytics", httpOnly, gesetzt vom Tracker) */
    private async _syncServerCookie(allowed: boolean): Promise<void> {
        const url = `${process.env.VUE_APP_API_PATH}/tracker/cookie`;
        try {
            if (allowed) {
                await axios.post(url, undefined, {withCredentials: true});
            } else {
                await axios.delete(url, {withCredentials: true});
            }
        } catch {
            // Einwilligung ist lokal gespeichert; der Server-Abgleich darf die UX nicht blockieren
        }
    }

    private _applyRevocations(previous: ConsentChoices, next: ConsentChoices): void {
        // GA4/TikTok werden in destinations.ts (Consent-Listener) gestoppt; hier
        // zusätzlich Cookies entfernen, auch wenn auf dieser Seite nichts geladen ist.
        if (!next.marketing) {
            removeCookies([...META_COOKIE_NAMES, ...TIKTOK_COOKIE_NAMES], TIKTOK_COOKIE_PREFIXES);
        }
        if (!next.statistics) {
            removeCookies(GA4_COOKIE_NAMES, GA4_COOKIE_PREFIXES);
        }
        if (!next.statistics && !next.marketing && (previous.statistics || previous.marketing)) {
            removeSignupSessionData();
            this._memorySids = {};
        }
    }

    /** Meta Pixel: Consent-API nutzen, falls das Pixel bereits geladen ist */
    private _applyMetaConsent(next: ConsentChoices): void {
        try {
            if (typeof window !== 'undefined' && window.fbq) {
                window.fbq('consent', next.marketing ? 'grant' : 'revoke');
            }
        } catch { /* noop */
        }
    }

    /* -------------------- Gates -------------------- */

    /** Meta-Browser-Pixel: nur auf /anmelden* oder /bewerben*, mit Marketing-Consent + geladenem Pixel */
    public canTrack(pathname: string): boolean {
        const fbqReady = typeof window !== 'undefined' && !!window.fbq;
        return (onSignupPath(pathname) || onRecruitingPath(pathname)) && this._consent.marketing && fbqReady;
    }

    /** Irgendeine Destination für diesen Pfad erlaubt? (Statistik oder Marketing) */
    private canTrackAny(pathname?: string): boolean {
        const path = pathname ?? getTrackingPath();
        return (onSignupPath(path) || onRecruitingPath(path)) && (this._consent.marketing || this._consent.statistics);
    }

    /**
     * Server-Mirroring (Tracker): fan-out an Meta CAPI + TikTok Events API
     * (Marketing) und auf /bewerben* zusätzlich an GA4 Measurement Protocol
     * (Statistik). Der Tracker kennt keine Kategorien → nur spiegeln, wenn
     * alle dort angesprochenen Kategorien erlaubt sind.
     */
    private canShareToServer(pathname?: string): boolean {
        const path = pathname ?? getTrackingPath();
        if (!onSignupPath(path) && !onRecruitingPath(path)) return false;
        if (!this._consent.marketing) return false;
        return onRecruitingPath(path) ? this._consent.statistics : true;
    }

    /* -------------------- Session helpers -------------------- */

    private _safeGetSessionItem(k: string): string | null {
        try {
            return sessionStorage.getItem(k);
        } catch {
            return null;
        }
    }

    private _safeSetSessionItem(k: string, v: string): void {
        try {
            sessionStorage.setItem(k, v);
        } catch { /* noop */
        }
    }

    private _safeRemoveSessionItem(k: string): void {
        try {
            sessionStorage.removeItem(k);
        } catch { /* noop */
        }
    }

    public getSignupSessionId(pathname?: string): string {
        const path = pathname ?? getTrackingPath();
        const pixelId = getPixelIdForPath(path) || 'default';
        const key = `signup.sid.${pixelId}`;

        const allowed = this._consent.marketing || this._consent.statistics;
        let sid = (allowed ? this._safeGetSessionItem(key) : null) || this._memorySids[key] || null;
        if (!sid) {
            sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        }
        this._memorySids[key] = sid;
        // Ohne Einwilligung nichts im Endgerät speichern (§ 25 TDDDG)
        if (allowed) this._safeSetSessionItem(key, sid);
        return sid;
    }

    private _getSentSet(pathname?: string): Set<string> {
        const sid = this.getSignupSessionId(pathname);
        const k = `signup.sent.${sid}`;
        const raw = this._safeGetSessionItem(k);
        let arr: string[] = [];
        if (raw) {
            try {
                arr = JSON.parse(raw) as string[];
            } catch {
                arr = [];
            }
        }
        return new Set(arr);
    }

    private _saveSentSet(set: Set<string>, pathname?: string): void {
        const sid = this.getSignupSessionId(pathname);
        const k = `signup.sent.${sid}`;
        this._safeSetSessionItem(k, JSON.stringify(Array.from(set)));
    }

    public resetSignupSession(): void {
        const sid = this._safeGetSessionItem('signup.sid');
        if (sid) this._safeRemoveSessionItem(`signup.sent.${sid}`);
        this._safeRemoveSessionItem('signup.sid');
    }

    private _save(): void {
        try {
            localStorage.setItem(COOKIE_SETTINGS_KEY, JSON.stringify(this._cookies));
        } catch { /* noop */
        }
    }

    /* -------------------- Meta context / event IDs -------------------- */

    private _getCookie(name: string): string | null {
        if (typeof document === 'undefined') return null;
        try {
            const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
            return m ? decodeURIComponent(m[2]) : null;
        } catch {
            return null;
        }
    }

    public getMetaCookies(): { fbp?: string; fbc?: string } {
        const fbp = this._getCookie('_fbp') || undefined;
        const fbc = this._getCookie('_fbc') || undefined;
        return {fbp, fbc};
    }

    public createEventId(label: string): string {
        const sid = this.getSignupSessionId();
        const counterKey = `signup.eid.${label}.${sid}`;
        const now = Date.now();
        const allowed = this._consent.marketing || this._consent.statistics;
        let n = 0;
        try {
            const stored = allowed ? this._safeGetSessionItem(counterKey) : null;
            n = parseInt(stored || this._memorySids[counterKey] || '0', 10) || 0;
        } catch { /* noop */
        }
        n += 1;
        this._memorySids[counterKey] = String(n);
        // Ohne Einwilligung nur im Speicher zählen (§ 25 TDDDG)
        if (allowed) this._safeSetSessionItem(counterKey, String(n));
        return `${label}.${sid}.${n}.${now}`;
    }

    public buildMetaContext(label: string) {
        const event_id = this.createEventId(label);
        const {fbp, fbc} = this.getMetaCookies();
        const sid = this.getSignupSessionId(getTrackingPath());

        const url = (typeof window !== 'undefined' ? window.location.href : '') || '';
        return {event_id, fbp, fbc, sid, url};
    }

    /* -------------------- Tracker (CAPI proxy) -------------------- */

    private async postMetaEventToTracker(body: TrackerEvent | TrackerEvent[]): Promise<void> {
        try {
            const base = process.env.VUE_APP_API_PATH || '';
            await axios.post(`${base}/tracker/meta-capi/events`, body, {
                withCredentials: true,
                timeout: 4000,
            });
        } catch {
            // silent – UX darf nicht leiden
        }
    }

    private buildTrackerEvent(
        evtName: string,
        customOrStdParams: Record<string, unknown> | undefined,
        pathname: string,
        label: string,
    ): undefined | { paramsWithId: Record<string, unknown>; trackerEvent: TrackerEvent } {
        const {event_id, fbp, fbc, url} = this.buildMetaContext(label);
        const paramsWithId = {...(customOrStdParams || {}), event_id};

        // 🧩 1) Pixel-ID anhand des Pfads bestimmen
        const pixelId = getPixelIdForPath(pathname);
        if (!pixelId) return;
        return {
            paramsWithId,
            trackerEvent: {
                event_name: evtName,
                event_time: Math.floor(Date.now() / 1000),
                event_id,
                pixel_id: pixelId,
                action_source: 'website',
                event_source_url: url,
                user_data: {fbp, fbc}, // IP/UA ergänzt der Server
                custom_data: customOrStdParams || {},
            },
        };
    }

    /** fbq senden (falls möglich) + immer (bei Consent) an Server spiegeln */
    private _dualSend(
        kind: 'track' | 'trackCustom',
        event: string,
        params: Record<string, unknown> | undefined,
        pathname: string,
        label: string, // Quelle für event_id
    ): void {
        if (!this.canTrackAny(pathname)) return;

        // 🧩 2) TrackerEvent zusammenbauen
        const eventResp = this.buildTrackerEvent(event, params, pathname, label);

        if (!eventResp) return;
        const {paramsWithId, trackerEvent} = eventResp;

        // 🧩 3) Browser Pixel-Event senden (nur wenn erlaubt)
        if (this.canTrack(pathname)) {
            try {
                window.fbq?.(kind as 'track', event, paramsWithId, {
                    eventID: paramsWithId.event_id as string | undefined,
                });
            } catch {
                /* noop */
            }
        }

        // 🧩 3b) GA4 User-Journey (Statistik; No-op, wenn für den Pfad keine Measurement-ID konfiguriert)
        if (this._consent.statistics) ga4Track(pathname, event, paramsWithId);

        // 🧩 3c) TikTok Browser-Pixel (Marketing; nur gemappte Events, Dedup mit S2S über event_id)
        if (this._consent.marketing) tiktokTrack(pathname, event, paramsWithId, paramsWithId.event_id as string | undefined);

        // 🧩 4) Mirror an Server (CAPI) – Tracker fanout't serverseitig an Meta, TikTok & GA4
        if (this.canShareToServer(pathname)) this.postMetaEventToTracker(trackerEvent);
    }

    /* -------------------- Öffentliche Tracking-APIs -------------------- */

    // Standard-Events: typsicher an MetaEventParamsMap
    public fbqSafeTrack<K extends MetaTrackEvents>(
        event: K,
        params?: MetaEventParamsMap[K],
        pathname?: string,
        label?: string,
    ): void {
        const path = pathname ?? getTrackingPath();
        const labelString = label || event.toString()
        this._dualSend('track', String(event), params as unknown as Record<string, unknown>, path, labelString);
    }

    public fbqTrackOnce<K extends MetaTrackEvents>(
        key: string,
        event: K,
        params?: MetaEventParamsMap[K],
        pathname?: string,
    ): void {
        const path = pathname ?? getTrackingPath();

        if (!this.canTrackAny(path)) return;


        const safeKey = key.length > 200 ? key.slice(0, 200) : key;
        const sent = this._getSentSet();

        if (sent.has(safeKey)) return;

        this._dualSend('track', String(event), params as unknown as Record<string, unknown>, path, safeKey);

        sent.add(safeKey);
        this._saveSentSet(sent);
    }

    public fbqSafeTrackCustom(
        event: string,
        params?: Record<string, unknown>,
        pathname?: string,
        label?: string,
    ): void {
        const path = pathname ?? getTrackingPath();
        const safeLabel = label || event;
        this._dualSend('trackCustom', event, params, path, safeLabel);
    }

    public fbqTrackCustomOnce(
        key: string,
        event: string,
        params?: Record<string, unknown>,
        pathname?: string,
    ): void {
        const path = pathname ?? getTrackingPath();
        if (!this.canTrackAny(path)) return;

        const safeKey = key.length > 200 ? key.slice(0, 200) : key;
        const sent = this._getSentSet();
        if (sent.has(safeKey)) return;

        this._dualSend('trackCustom', event, params, path, safeKey);

        sent.add(safeKey);
        this._saveSentSet(sent);
    }

    public fbqTrackIfChanged<K extends MetaTrackEvents>(
        sessionKey: string,
        currentValue: string,
        event: K,
        buildParams: (prev: string | null) => MetaEventParamsMap[K],
        pathname?: string,
    ): void {
        const path = pathname ?? getTrackingPath();
        if (!this.canTrackAny(path)) return;

        const storageKey = `signup.changed.${sessionKey}`;
        const prev = this._safeGetSessionItem(storageKey);
        if (prev === currentValue) return;

        try {
            const params = buildParams(prev);
            const label = `${String(event)}:${sessionKey}`;
            this._dualSend('track', String(event), params as unknown as Record<string, unknown>, path, label);
            this._safeSetSessionItem(storageKey, currentValue);
        } catch {
            // noop
        }
    }

    /**
     * TikTok Advanced Matching: PII wird client-seitig SHA-256-gehasht an
     * ttq.identify übergeben. VOR dem zugehörigen Event aufrufen
     * (z.B. Kontaktdaten vor ContactCompleted, employee_id vor Lead).
     * No-op ohne Consent oder außerhalb von /bewerben*.
     */
    public tiktokIdentify(pii: TikTokIdentifyPii, pathname?: string): void {
        const path = pathname ?? getTrackingPath();
        if (!this.canTrackAny(path) || !this._consent.marketing) return;
        tiktokIdentify(path, pii).catch(() => { /* noop */ });
    }

    public fbqTrackCustomIfChanged(
        key: string,
        value: string,
        event: string,
        paramsBuilder: (prev?: string) => Record<string, unknown>,
        pathname?: string,
    ): void {
        const path = pathname ?? getTrackingPath();
        if (!this.canTrackAny(path)) return;

        const sid = this.getSignupSessionId();
        const k = `signup.custom.${key}.${sid}`;
        const prev = this._safeGetSessionItem(k);
        if (prev === value) return;

        try {
            const params = paramsBuilder(prev ?? undefined);
            const label = `${event}:${key}`;
            this._dualSend('trackCustom', event, params, path, label);
            this._safeSetSessionItem(k, value);
        } catch {
            // noop
        }
    }
}
